import { type VariablesOf } from '@graphql-typed-document-node/core'
import { useAtomValue, useSetAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'

import { brandGroupIdAtom, surveyFilterAtom } from '~/atoms'
import { graphql } from '~/gql'
import { useTabulate } from '~/hooks/useTabulate'
import { exists } from '~/utils'

import {
  brand1IdAtom,
  brand2IdAtom,
  commentAtom,
  customerAttributeIdsAtom,
  isPendingTabulationAtom,
  isTabulationReadyAtom,
  lastTabulatedVariablesAtom,
  overlapGroupFiltersAtom,
  questionIdsAtom,
  savedCommentAtom,
  tabulationQueryDataAtom,
} from '../atoms'

import { useSurveyId } from './useSurveyId'

const mutationDocument = graphql(`
  mutation OverlapAnalysisPage_TabulationMutation(
    $surveyId: String!
    $brand1Id: Int!
    $brand2Id: Int!
    $overlapGroupFilters: [OverlapGroupFilter!]
    $questionIds: [String!]!
    $customerAttributeIds: [ID!]!
    $filter: [[CustomerAttributeFilterItemInput!]!]!
  ) {
    count: countOverlapAnalysis(
      surveyId: $surveyId
      overlap: { brand1: $brand1Id, brand2: $brand2Id }
      overlapGroupFilters: $overlapGroupFilters
      answers: { questionIds: $questionIds }
      customerAttributeIds: $customerAttributeIds
      customFilter: { filter: $filter }
    ) {
      awaitId
    }
  }
`)
type MutationVariables = VariablesOf<typeof mutationDocument>

const queryDocument = graphql(`
  query OverlapAnalysisPage_Tabulation($awaitId: String!) {
    count: countOverlapAnalysis(awaitId: $awaitId) {
      arrived
      result {
        ...OverlapAnalysisPage_TabulationQueryData
      }
    }
  }
`)

export const useTabulationQuery = () => {
  const isTabulationReady = useAtomValue(isTabulationReadyAtom)
  const isPendingTabulation = useAtomValue(isPendingTabulationAtom)

  const surveyFilter = useAtomValue(surveyFilterAtom)
  const brandGroupId = useAtomValue(brandGroupIdAtom)
  const brand1Id = useAtomValue(brand1IdAtom)
  const brand2Id = useAtomValue(brand2IdAtom)
  const surveyId = useSurveyId()
  const overlapGroupFilters = useAtomValue(overlapGroupFiltersAtom)
  const questionIds = useAtomValue(questionIdsAtom)
  const customerAttributeIds = useAtomValue(customerAttributeIdsAtom)

  const [tabulateState, tabulate] = useTabulate(mutationDocument, queryDocument)

  const setTabulationQueryData = useSetAtom(tabulationQueryDataAtom)
  const setLastTabulatedVariables = useSetAtom(lastTabulatedVariablesAtom)
  const resetComment = useResetAtom(commentAtom)
  const resetSavedComment = useResetAtom(savedCommentAtom)

  const hasTabulatedRef = React.useRef(false)

  const tabulateIfNeeded = React.useCallback(async () => {
    if (
      !isTabulationReady ||
      isPendingTabulation ||
      !exists(brandGroupId) ||
      !exists(brand1Id) ||
      !exists(brand2Id)
    ) {
      return
    }

    const variables: MutationVariables = {
      surveyId,
      brand1Id,
      brand2Id,
      overlapGroupFilters,
      questionIds,
      customerAttributeIds,
      filter: surveyFilter?.filterGroups ?? [],
    }
    const queryData = await tabulate(variables)

    setTabulationQueryData(queryData)
    setLastTabulatedVariables(variables)

    // 再集計時は古いコメントが新しいデータに残らないようリセットする。
    // 初回集計（URL 復元したコメントを保持）ではリセットしない。
    if (hasTabulatedRef.current) {
      resetComment()
      resetSavedComment()
    }
    hasTabulatedRef.current = true
  }, [
    brand1Id,
    brand2Id,
    brandGroupId,
    customerAttributeIds,
    isPendingTabulation,
    isTabulationReady,
    overlapGroupFilters,
    questionIds,
    resetComment,
    resetSavedComment,
    setLastTabulatedVariables,
    setTabulationQueryData,
    surveyFilter?.filterGroups,
    surveyId,
    tabulate,
  ])

  React.useEffect(() => {
    tabulateIfNeeded()
  }, [tabulateIfNeeded])

  return tabulateState.loading
}
